/**
 * <luboo-dialog> — consolidated modal dialog component.
 *
 * Usage:
 *   <luboo-dialog id="publish-confirm" heading="Publish draft">
 *     <p>Draft "<span class="draft-name"></span>" will be published.</p>
 *     <button slot="actions" value="cancel">Cancel</button>
 *     <button slot="actions" value="confirm" type="submit">Publish</button>
 *   </luboo-dialog>
 *
 *   const dlg = document.getElementById('publish-confirm');
 *   const result = await dlg.open((d) => {
 *     d.querySelector('.draft-name').textContent = currentDraft;
 *   });
 *   if (result === 'confirm') { ... }
 *
 * Attributes:
 *   heading           — text for the h2 at the top of the dialog
 *   no-close-button   — suppress the default top-right close button
 *
 * The internal native <dialog> is appended to document.body on connect so it
 * always renders in the top layer — shadow-DOM slot nesting cannot produce
 * 0×0 dimensions. The native <dialog> keeps ESC + returnValue behavior; the
 * component adds backdrop-click → close('cancel'), focus restoration, and a
 * Promise-based open() API.
 */

let _headingIdCounter = 0;

class LubooDialog extends HTMLElement {
  constructor() {
    super();
    this._dialog = null;
    this._bodyEl = null;
    this._actionsEl = null;
    this._returnFocusEl = null;
    this._currentResolve = null;
  }

  connectedCallback() {
    if (this._dialog) return;
    this._buildDialog();
  }

  _buildDialog() {
    const heading = this.getAttribute('heading') || '';

    const dialog = document.createElement('dialog');
    dialog.className = 'workspace-dialog luboo-dialog';
    dialog.setAttribute('role', 'dialog');

    // Propagate any classes from the host onto the internal <dialog> so
    // per-instance CSS hooks like .about-dialog or .deploy-template-dialog
    // keep targeting the rendered element.
    for (const cls of this.classList) dialog.classList.add(cls);

    // Heading comes from either slot="heading" children (rich content — e.g.
    // <svg-icon> + label) or the `heading` attribute (plain text fallback).
    const headingSlotChildren = Array.from(this.childNodes).filter(
      (n) => n.nodeType === Node.ELEMENT_NODE && n.getAttribute('slot') === 'heading',
    );
    if (headingSlotChildren.length > 0) {
      const h2 = document.createElement('h2');
      h2.id = `luboo-dialog-heading-${++_headingIdCounter}`;
      for (const node of headingSlotChildren) {
        node.removeAttribute('slot');
        h2.appendChild(node);
      }
      dialog.setAttribute('aria-labelledby', h2.id);
      dialog.appendChild(h2);
    } else if (heading) {
      const h2 = document.createElement('h2');
      h2.id = `luboo-dialog-heading-${++_headingIdCounter}`;
      h2.textContent = heading;
      dialog.setAttribute('aria-labelledby', h2.id);
      dialog.appendChild(h2);
    }

    if (!this.hasAttribute('no-close-button')) {
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'luboo-dialog-close';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.innerHTML = '&times;';
      closeBtn.addEventListener('click', () => dialog.close('cancel'));
      dialog.appendChild(closeBtn);
    }

    const body = document.createElement('div');
    body.className = 'luboo-dialog-body';
    dialog.appendChild(body);

    const form = document.createElement('form');
    form.method = 'dialog';
    // Secondary footer slot — left-side actions like "New Folder" that stay
    // inside the footer row but don't participate in the primary Cancel/Confirm pair.
    const actionsSecondary = document.createElement('div');
    actionsSecondary.className = 'dialog-buttons-secondary';
    form.appendChild(actionsSecondary);
    const actions = document.createElement('div');
    actions.className = 'dialog-buttons';
    form.appendChild(actions);
    dialog.appendChild(form);

    // Move host children into body, primary actions footer, or secondary footer.
    const children = Array.from(this.childNodes);
    for (const node of children) {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        body.appendChild(node);
        continue;
      }
      const slot = node.getAttribute('slot');
      if (slot === 'actions') {
        node.removeAttribute('slot');
        this._prepareActionButton(node);
        actions.appendChild(node);
      } else if (slot === 'actions-secondary') {
        node.removeAttribute('slot');
        // Secondary actions are buttons that should NOT submit the form (they
        // perform a sub-action without closing). Default to type="button".
        if (node.tagName === 'BUTTON' && !node.hasAttribute('type')) {
          node.setAttribute('type', 'button');
        }
        actionsSecondary.appendChild(node);
      } else {
        body.appendChild(node);
      }
    }

    // type="button" action buttons don't submit the form — wire their value manually.
    actions.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn || !actions.contains(btn)) return;
      if (btn.type === 'button' && btn.value) {
        dialog.close(btn.value);
      }
    });

    // Backdrop click → cancel. Uses bounding-rect check (matches workspace-manager
    // pattern) and guards the 0,0 edge case from native select elements.
    dialog.addEventListener('click', (e) => {
      if (e.target !== dialog) return;
      if (e.clientX === 0 && e.clientY === 0) return;
      const rect = dialog.getBoundingClientRect();
      const outside =
        e.clientY < rect.top ||
        e.clientY > rect.top + rect.height ||
        e.clientX < rect.left ||
        e.clientX > rect.left + rect.width;
      if (outside) dialog.close('cancel');
    });

    dialog.addEventListener('close', () => {
      const returnValue = dialog.returnValue || 'cancel';
      dialog.returnValue = '';
      if (this._returnFocusEl && typeof this._returnFocusEl.focus === 'function') {
        try {
          this._returnFocusEl.focus();
        } catch (_) {
          // ignore — element may have been removed
        }
      }
      this._returnFocusEl = null;
      if (this._currentResolve) {
        const resolve = this._currentResolve;
        this._currentResolve = null;
        resolve(returnValue);
      }
    });

    document.body.appendChild(dialog);
    this._dialog = dialog;
    this._bodyEl = body;
    this._actionsEl = actions;
  }

  _prepareActionButton(btn) {
    if (btn.tagName !== 'BUTTON') return;
    if (!btn.classList.contains('dialog-button')) btn.classList.add('dialog-button');
    const value = btn.getAttribute('value') || '';
    if (value === 'confirm' && !btn.classList.contains('dialog-confirm')) {
      btn.classList.add('dialog-confirm');
    }
    if ((value === 'cancel' || value === '') && !btn.classList.contains('dialog-cancel')) {
      btn.classList.add('dialog-cancel');
      if (!btn.hasAttribute('type')) btn.setAttribute('type', 'button');
      if (!btn.hasAttribute('value')) btn.setAttribute('value', 'cancel');
    }
  }

  /**
   * Open the dialog. Returns a Promise that resolves with the close value
   * ('confirm', 'cancel', or any custom value passed to close()).
   *
   * @param {(dialog: HTMLDialogElement) => void} [beforeShow]
   *   Optional callback invoked with the internal <dialog> before showModal.
   *   Use it to mutate content (e.g. set a draft name) just before display.
   * @returns {Promise<string>}
   */
  open(beforeShow) {
    if (!this._dialog) this._buildDialog();
    this._returnFocusEl = document.activeElement;
    if (typeof beforeShow === 'function') {
      beforeShow(this._dialog);
    }
    return new Promise((resolve) => {
      this._currentResolve = resolve;
      this._dialog.showModal();
    });
  }

  /**
   * Programmatically close the dialog with an optional return value.
   * @param {string} [value]
   */
  close(value) {
    if (this._dialog && this._dialog.open) this._dialog.close(value);
  }

  /** The internal native <dialog> element (attached to document.body). */
  get dialog() {
    if (!this._dialog) this._buildDialog();
    return this._dialog;
  }

  /**
   * True when the dialog is currently open (mirrors native dialog.open).
   * Note: intentionally not named `open` — that would collide with the open()
   * method on the prototype. Use .isOpen for the boolean state, .open() to
   * show the dialog.
   */
  get isOpen() {
    return this._dialog?.open ?? false;
  }

  /** The body container inside the dialog — where default children live. */
  get body() {
    if (!this._bodyEl) this._buildDialog();
    return this._bodyEl;
  }

  /** Query inside the dialog — children live in the body-attached <dialog>, not the host. */
  querySelector(selector) {
    if (!this._dialog) this._buildDialog();
    return this._dialog.querySelector(selector);
  }

  querySelectorAll(selector) {
    if (!this._dialog) this._buildDialog();
    return this._dialog.querySelectorAll(selector);
  }

  /**
   * Destroy the dialog (remove body-attached <dialog>, detach host).
   * Used by the dynamic alert/confirm/prompt helpers to clean up.
   */
  _destroy() {
    if (this._dialog && this._dialog.parentElement) {
      this._dialog.parentElement.removeChild(this._dialog);
    }
    this._dialog = null;
    this._bodyEl = null;
    this._actionsEl = null;
    if (this.parentElement) this.parentElement.removeChild(this);
  }

  // ============================================================
  // Static helpers — dynamic replacements for alert/confirm/prompt
  // ============================================================

  /**
   * Show an informational dialog with a single OK button. Replaces window.alert.
   *
   * @param {string} message — body text (newlines become paragraph breaks).
   * @param {object} [opts]
   * @param {string} [opts.heading] — heading text (defaults to none).
   * @param {string} [opts.okLabel='OK'] — OK button label.
   * @returns {Promise<void>} resolves when the dialog closes.
   */
  static async alert(message, opts = {}) {
    const dlg = LubooDialog._makeTransient({
      heading: opts.heading || '',
      message,
      buttons: [{ value: 'confirm', label: opts.okLabel || 'OK', kind: 'confirm', submit: true }],
    });
    try {
      await dlg.open();
    } finally {
      dlg._destroy();
    }
  }

  /**
   * Show a yes/no confirmation dialog. Replaces window.confirm.
   *
   * @param {string} message — body text.
   * @param {object} [opts]
   * @param {string} [opts.heading]
   * @param {string} [opts.okLabel='OK']
   * @param {string} [opts.cancelLabel='Cancel']
   * @param {boolean} [opts.danger=false] — style OK as destructive.
   * @returns {Promise<boolean>} true on confirm, false on cancel/backdrop/ESC.
   */
  static async confirm(message, opts = {}) {
    const dlg = LubooDialog._makeTransient({
      heading: opts.heading || '',
      message,
      buttons: [
        { value: 'cancel', label: opts.cancelLabel || 'Cancel', kind: 'cancel' },
        {
          value: 'confirm',
          label: opts.okLabel || 'OK',
          kind: opts.danger ? 'danger' : 'confirm',
          submit: true,
        },
      ],
    });
    try {
      const result = await dlg.open();
      return result === 'confirm';
    } finally {
      dlg._destroy();
    }
  }

  /**
   * Show a text-input dialog. Replaces window.prompt.
   *
   * @param {string} message — body text / label for the input.
   * @param {object} [opts]
   * @param {string} [opts.heading]
   * @param {string} [opts.defaultValue='']
   * @param {string} [opts.placeholder]
   * @param {string} [opts.type='text'] — input type (e.g. 'password').
   * @param {string} [opts.okLabel='OK']
   * @param {string} [opts.cancelLabel='Cancel']
   * @returns {Promise<string|null>} entered value, or null if cancelled.
   */
  static async prompt(message, opts = {}) {
    const input = document.createElement('input');
    input.type = opts.type || 'text';
    input.className = 'dialog-input luboo-dialog-prompt-input';
    input.value = opts.defaultValue != null ? opts.defaultValue : '';
    if (opts.placeholder) input.placeholder = opts.placeholder;

    const dlg = LubooDialog._makeTransient({
      heading: opts.heading || '',
      message,
      extraBodyNodes: [input],
      buttons: [
        { value: 'cancel', label: opts.cancelLabel || 'Cancel', kind: 'cancel' },
        { value: 'confirm', label: opts.okLabel || 'OK', kind: 'confirm', submit: true },
      ],
    });

    // Submit on Enter inside the input.
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        dlg.close('confirm');
      }
    });

    try {
      const result = await dlg.open(() => {
        // Focus + select after showModal so focus trap doesn't fight us.
        queueMicrotask(() => {
          input.focus();
          input.select();
        });
      });
      return result === 'confirm' ? input.value : null;
    } finally {
      dlg._destroy();
    }
  }

  /**
   * Destructive-action confirmation that requires typing a specific phrase
   * (typically a slug or name) before the danger button enables. Same shape
   * as `prompt`, but the confirm button is disabled until input matches.
   *
   * @param {string} message — body text shown above the input.
   * @param {object} opts
   * @param {string} opts.requiredText — exact string the user must type.
   * @param {string} [opts.heading]
   * @param {string} [opts.placeholder] — defaults to requiredText.
   * @param {string} [opts.okLabel='Delete']
   * @param {string} [opts.cancelLabel='Cancel']
   * @returns {Promise<boolean>} true if confirmed (only possible on match), false otherwise.
   */
  static async confirmTyped(message, opts = {}) {
    if (!opts.requiredText) {
      throw new Error('LubooDialog.confirmTyped: opts.requiredText is required');
    }
    const required = opts.requiredText;

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'dialog-input luboo-dialog-prompt-input';
    input.placeholder = opts.placeholder || required;
    input.autocomplete = 'off';
    input.setAttribute('aria-label', `Type ${required} to confirm`);

    const dlg = LubooDialog._makeTransient({
      heading: opts.heading || '',
      message,
      extraBodyNodes: [input],
      buttons: [
        { value: 'cancel', label: opts.cancelLabel || 'Cancel', kind: 'cancel' },
        { value: 'confirm', label: opts.okLabel || 'Delete', kind: 'danger', submit: true },
      ],
    });

    const confirmBtn = dlg.querySelector('button[value="confirm"]');
    if (confirmBtn) confirmBtn.disabled = true;

    input.addEventListener('input', () => {
      const matches = input.value === required;
      if (confirmBtn) confirmBtn.disabled = !matches;
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && input.value === required) {
        e.preventDefault();
        dlg.close('confirm');
      }
    });

    try {
      const result = await dlg.open(() => {
        queueMicrotask(() => input.focus());
      });
      return result === 'confirm';
    } finally {
      dlg._destroy();
    }
  }

  /**
   * Internal: build a transient <luboo-dialog>, attach to body, ready to open.
   * @private
   */
  static _makeTransient({ heading, message, buttons, extraBodyNodes }) {
    const dlg = document.createElement('luboo-dialog');
    if (heading) dlg.setAttribute('heading', heading);

    // Message — split on newlines to produce paragraph breaks.
    if (message) {
      const lines = String(message).split(/\n\n+|\n/);
      for (const line of lines) {
        if (!line) continue;
        const p = document.createElement('p');
        p.textContent = line;
        dlg.appendChild(p);
      }
    }

    if (extraBodyNodes) {
      for (const node of extraBodyNodes) dlg.appendChild(node);
    }

    for (const btn of buttons) {
      const el = document.createElement('button');
      el.setAttribute('slot', 'actions');
      el.setAttribute('value', btn.value);
      if (btn.submit) el.setAttribute('type', 'submit');
      if (btn.kind === 'danger') el.classList.add('dialog-danger');
      el.textContent = btn.label;
      dlg.appendChild(el);
    }

    document.body.appendChild(dlg); // triggers connectedCallback → build
    return dlg;
  }
}

export { LubooDialog };
